import React from 'react'
import { MessageSquare, UserPlus, CheckCircle2 } from 'lucide-react'
import { Button } from './ui/button'
import { Separator } from './ui/separator'
import { Person, AssistantConnection } from '../types/profile'
import { useToast } from './ui/toast'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { cn } from '../lib/utils'

interface PersonDetailPanelProps {
  person: Person | null
  isOpen: boolean
  onClose: () => void
  onTalkToAssistant: (person: Person) => void
  currentUserAccessList?: AssistantConnection[]
}

export const PersonDetailPanel: React.FC<PersonDetailPanelProps> = ({
  person,
  isOpen,
  onClose,
  onTalkToAssistant,
  currentUserAccessList = [],
}) => {
  const { addToast } = useToast()
  const [showRequestDialog, setShowRequestDialog] = React.useState(false)

  if (!person) return null

  // Check if current user has access to this person's assistant
  const hasAccess = currentUserAccessList.some(
    connection => connection.personName === person.name
  )

  const handleRequestAccess = () => {
    setShowRequestDialog(true)
  }

  const handleConfirmRequest = () => {
    setShowRequestDialog(false)
    addToast({
      title: 'Access request sent',
      description: `Your request to access ${person.name}'s assistant has been sent.`,
      variant: 'success',
    })
    onClose()
  }

  const handleTalkToAssistant = () => {
    onTalkToAssistant(person)
    onClose()
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{person.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium">Title</span>
                <p className="text-sm text-muted-foreground">{person.title}</p>
              </div>
              <div>
                <span className="text-sm font-medium">Department</span>
                <p className="text-sm text-muted-foreground">{person.department}</p>
              </div>
              {person.reportsTo && (
                <div>
                  <span className="text-sm font-medium">Reports To</span>
                  <p className="text-sm text-muted-foreground">{person.reportsTo}</p>
                </div>
              )}
            </div>

            <Separator />

            <div>
              <h4 className="text-sm font-semibold mb-3">Assistant Access</h4>
              {hasAccess ? (
                <div className="flex items-start gap-3 p-3 rounded-lg border bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
                  <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0 text-green-600 dark:text-green-500" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">You have access</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      You can talk to {person.name}'s assistant
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/50">
                  <UserPlus className="h-5 w-5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">No access</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Request access to talk to {person.name}'s assistant
                    </p>
                  </div>
                </div>
              )}
            </div>

            <Separator />

            <div className="flex gap-2">
              {hasAccess ? (
                <Button
                  onClick={handleTalkToAssistant}
                  className="w-full"
                  variant="default"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Talk to Assistant
                </Button>
              ) : (
                <Button
                  onClick={handleRequestAccess}
                  className="w-full"
                  variant="default"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Request Access
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Access Confirmation Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Request Access</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to request access to {person.name}'s assistant?
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowRequestDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmRequest}>
              Send Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

